/**
 * FullPageEditorLayout Component
 *
 * A full-page layout for multi-step editors (replacing modal-based wizards).
 * Provides the same functionality as SplitScreenWizardModal but as a routable page.
 *
 * Features:
 * - Split screen: Editor form (left) + Live preview (right)
 * - Step navigation via sidebar or progress bar
 * - Device mode switching for preview (Desktop/Tablet/Mobile)
 * - Unsaved changes protection
 * - URL-based navigation (bookmarkable, shareable)
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Monitor,
  Smartphone,
  Tablet,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Loader2,
} from 'lucide-react';
import { Button } from '@/client/components/ui/button';
import { cn } from '@/client/lib/utils';
import { WizardProvider, useWizard, type WizardStepConfig } from '@/client/components/wizard';
import { WizardNavigation } from '@/client/components/wizard/wizard-navigation';

// =============================================================================
// Types
// =============================================================================

type DeviceMode = 'desktop' | 'tablet' | 'mobile';

const DEVICE_DIMENSIONS = {
  desktop: { width: '100%', height: '100%' },
  tablet: { width: '768px', height: '1024px' },
  mobile: { width: '375px', height: '667px' },
};

export interface FullPageEditorLayoutProps {
  /** Page title displayed in header */
  title: string;
  /** Optional subtitle */
  subtitle?: string;
  /** Step configurations */
  steps: WizardStepConfig[];
  /** Content to render (should contain WizardStep components) */
  children: React.ReactNode;
  /** Initial step index */
  initialStep?: number;
  /** Whether in edit mode (existing item) vs create mode */
  isEditMode?: boolean;
  /** Callback when wizard completes (publish/save) */
  onComplete: () => Promise<void>;
  /** Callback for save as draft */
  onSaveDraft?: () => Promise<void>;
  /** Whether save as draft is available */
  showSaveDraft?: boolean;
  /** Custom complete button text */
  completeButtonText?: string;
  /** Accent color theme */
  accentColor?: 'emerald' | 'violet' | 'blue' | 'amber' | 'rose';
  /** HTML content for live preview */
  previewHtml?: string;
  /** Whether to show the preview pane */
  hasPreview?: boolean;
  /** Back navigation path (defaults to going back) */
  backPath?: string;
  /** Whether form has unsaved changes */
  isDirty?: boolean;
  /** Whether data is loading */
  isLoading?: boolean;
  /** Step validation function */
  validateStep?: (stepIndex: number) => Promise<boolean> | boolean;
}

// =============================================================================
// Live Preview Pane
// =============================================================================

function LivePreviewPane({
  html,
  deviceMode,
  onDeviceModeChange,
  zoom,
  onZoomChange,
}: {
  html?: string;
  deviceMode: DeviceMode;
  onDeviceModeChange: (mode: DeviceMode) => void;
  zoom: number;
  onZoomChange: (zoom: number) => void;
}) {
  const dimensions = DEVICE_DIMENSIONS[deviceMode];

  return (
    <div className="flex flex-col h-full bg-muted/30">
      {/* Preview Controls */}
      <div className="flex items-center justify-between gap-4 px-4 py-3 border-b bg-background/95 backdrop-blur-sm">
        <div className="flex items-center gap-1">
          <span className="text-xs font-medium text-muted-foreground mr-2">Device:</span>
          <Button
            variant={deviceMode === 'desktop' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => onDeviceModeChange('desktop')}
            className="h-8 px-3 gap-1.5"
            title="Desktop view"
          >
            <Monitor className="h-3.5 w-3.5" />
            <span className="hidden sm:inline text-xs">Desktop</span>
          </Button>
          <Button
            variant={deviceMode === 'tablet' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => onDeviceModeChange('tablet')}
            className="h-8 px-3 gap-1.5"
            title="Tablet view"
          >
            <Tablet className="h-3.5 w-3.5" />
            <span className="hidden sm:inline text-xs">Tablet</span>
          </Button>
          <Button
            variant={deviceMode === 'mobile' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => onDeviceModeChange('mobile')}
            className="h-8 px-3 gap-1.5"
            title="Mobile view"
          >
            <Smartphone className="h-3.5 w-3.5" />
            <span className="hidden sm:inline text-xs">Mobile</span>
          </Button>
        </div>

        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => onZoomChange(Math.max(0.5, zoom - 0.1))}
            disabled={zoom <= 0.5}
            className="h-8 w-8"
            title="Zoom out"
          >
            <ZoomOut className="h-3.5 w-3.5" />
          </Button>
          <span className="text-xs font-medium text-muted-foreground min-w-[3rem] text-center">
            {Math.round(zoom * 100)}%
          </span>
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => onZoomChange(Math.min(1.5, zoom + 0.1))}
            disabled={zoom >= 1.5}
            className="h-8 w-8"
            title="Zoom in"
          >
            <ZoomIn className="h-3.5 w-3.5" />
          </Button>
          <div className="w-px h-5 bg-border mx-1" />
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => onZoomChange(1)}
            className="h-8 w-8"
            title="Reset zoom"
          >
            <Maximize2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* Preview Area */}
      <div className="flex-1 overflow-auto p-6 flex items-center justify-center">
        <div
          className={cn(
            'bg-white dark:bg-zinc-900 rounded-xl shadow-2xl border-2 border-border overflow-hidden transition-all duration-300',
            deviceMode === 'desktop' && 'w-full h-full',
            deviceMode === 'tablet' && 'max-w-[768px] max-h-[1024px]',
            deviceMode === 'mobile' && 'max-w-[375px] max-h-[667px]'
          )}
          style={{
            width: deviceMode === 'desktop' ? '100%' : dimensions.width,
            height: deviceMode === 'desktop' ? '100%' : dimensions.height,
            transform: `scale(${zoom})`,
            transformOrigin: 'center center',
          }}
        >
          {html ? (
            <iframe
              srcDoc={html}
              className="w-full h-full border-0"
              title="Page Preview"
              sandbox="allow-scripts allow-same-origin"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center p-8 text-center">
              <div>
                <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <Monitor className="h-8 w-8 text-white" />
                </div>
                <p className="text-sm text-muted-foreground">Preview will appear here</p>
                <p className="text-xs text-muted-foreground/70 mt-1">
                  Fill out the form to see your page come to life
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Preview Info Footer */}
      <div className="px-4 py-2 border-t bg-background/50 backdrop-blur-sm">
        <p className="text-xs text-muted-foreground text-center">
          {deviceMode === 'desktop' && 'Viewing full desktop layout'}
          {deviceMode === 'tablet' && 'Viewing tablet layout (768×1024)'}
          {deviceMode === 'mobile' && 'Viewing mobile layout (375×667)'}
        </p>
      </div>
    </div>
  );
}

// =============================================================================
// Step Sidebar
// =============================================================================

function StepSidebar() {
  const { steps, currentStep, completedSteps, goToStep, canNavigateTo } = useWizard();

  return (
    <nav className="w-56 border-r bg-muted/20 p-4 space-y-1 flex-shrink-0">
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 px-3">
        Steps
      </h3>
      {steps.map((step, index) => {
        const isComplete = completedSteps.has(index);
        const isCurrent = currentStep === index;
        const isClickable = canNavigateTo(index);
        const Icon = step.icon;

        return (
          <button
            key={step.id}
            type="button"
            onClick={() => isClickable && goToStep(index)}
            disabled={!isClickable}
            className={cn(
              'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all text-left',
              'focus:outline-none focus:ring-2 focus:ring-primary/50',
              isCurrent && 'bg-primary text-primary-foreground shadow-sm',
              isComplete && !isCurrent && 'text-primary hover:bg-primary/10',
              !isCurrent && !isComplete && 'text-muted-foreground',
              isClickable && !isCurrent && 'hover:bg-muted cursor-pointer',
              !isClickable && 'cursor-not-allowed opacity-50'
            )}
          >
            <div
              className={cn(
                'w-7 h-7 rounded-full flex items-center justify-center text-xs flex-shrink-0',
                isCurrent && 'bg-primary-foreground/20',
                isComplete && !isCurrent && 'bg-primary/10',
                !isCurrent && !isComplete && 'bg-muted'
              )}
            >
              <Icon className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="truncate">{step.label}</div>
              {step.optional && <div className="text-xs opacity-70">Optional</div>}
            </div>
            {isComplete && !isCurrent && (
              <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
            )}
          </button>
        );
      })}
    </nav>
  );
}

// =============================================================================
// Main Layout Component
// =============================================================================

function EditorLayoutContent({
  title,
  subtitle,
  children,
  onComplete,
  onSaveDraft,
  showSaveDraft = false,
  completeButtonText = 'Publish',
  accentColor = 'violet',
  previewHtml,
  hasPreview = false,
  backPath,
  isDirty = false,
}: Omit<
  FullPageEditorLayoutProps,
  'steps' | 'initialStep' | 'isEditMode' | 'isLoading' | 'validateStep'
>) {
  const navigate = useNavigate();
  const [deviceMode, setDeviceMode] = useState<DeviceMode>('desktop');
  const [zoom, setZoom] = useState(1);

  // Warn on page unload (browser refresh/close)
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  const handleBack = useCallback(() => {
    // Check for unsaved changes before navigating
    if (isDirty) {
      // eslint-disable-next-line no-alert
      const confirmed = window.confirm('You have unsaved changes. Are you sure you want to leave?');
      if (!confirmed) {
        return;
      }
    }
    if (backPath) {
      navigate(backPath);
    } else {
      navigate(-1);
    }
  }, [navigate, backPath, isDirty]);

  // Accent color gradients
  const accentGradients: Record<string, string> = {
    emerald: 'from-emerald-500/5 to-transparent',
    violet: 'from-violet-500/5 to-transparent',
    blue: 'from-blue-500/5 to-transparent',
    amber: 'from-amber-500/5 to-transparent',
    rose: 'from-rose-500/5 to-transparent',
  };

  const headerGradient = accentGradients[accentColor] || accentGradients.violet;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Header with Navigation */}
      <header
        className={cn(
          'flex items-center justify-between gap-4 px-4 sm:px-6 py-3 border-b',
          `bg-gradient-to-r ${headerGradient}`
        )}
      >
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="ghost" size="sm" onClick={handleBack} className="gap-2 flex-shrink-0">
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Back</span>
          </Button>
          <div className="h-5 w-px bg-border hidden sm:block" />
          <div className="min-w-0">
            <h1 className="text-lg font-semibold truncate">{title}</h1>
            {subtitle && <p className="text-xs text-muted-foreground truncate">{subtitle}</p>}
          </div>
        </div>

        {/* Navigation buttons in header */}
        <WizardNavigation
          completeButtonText={completeButtonText}
          showSaveDraft={showSaveDraft}
          onSaveDraft={onSaveDraft}
          onComplete={onComplete}
          variant="header"
        />
      </header>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Step Sidebar - hidden on mobile */}
        <div className="hidden lg:block">
          <StepSidebar />
        </div>

        {/* Editor + Preview Split */}
        <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
          {/* Editor Form Area */}
          <div className={cn('flex-1 flex flex-col overflow-hidden', hasPreview && 'lg:w-[45%]')}>
            <div className="flex-1 overflow-y-auto p-4 sm:p-6">{children}</div>
          </div>

          {/* Preview Pane - hidden on mobile */}
          {hasPreview && (
            <div className="hidden lg:flex lg:w-[55%] border-l">
              <LivePreviewPane
                html={previewHtml}
                deviceMode={deviceMode}
                onDeviceModeChange={setDeviceMode}
                zoom={zoom}
                onZoomChange={setZoom}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Exported Component with Provider
// =============================================================================

export function FullPageEditorLayout({
  steps,
  initialStep = 0,
  isEditMode = false,
  isLoading = false,
  validateStep,
  ...props
}: FullPageEditorLayoutProps) {
  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <WizardProvider
      steps={steps}
      initialStep={initialStep}
      isEditMode={isEditMode}
      validateStep={validateStep}
    >
      <EditorLayoutContent {...props} />
    </WizardProvider>
  );
}
